import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import { Crown, ShoppingBag, Lightning, Sparkle, ArrowRight } from '@phosphor-icons/react';

const CauseChip = ({ active, label, onClick, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition ${
      active ? 'bg-bx-volt text-black' : 'bg-white/5 text-white/70 hover:bg-white/10'
    }`}
  >
    {label}
  </button>
);

const DropCard = ({ d, causes, onBuy }) => {
  const [cause, setCause] = useState(causes[0]?.id || '');
  const remaining = d.supply - (d.sold || 0);
  const soldOut = remaining <= 0;
  return (
    <motion.div
      whileHover={{ y: -3 }}
      className="glass rounded-2xl p-6 flex flex-col"
      data-testid={`drop-card-${d.id}`}
    >
      <div className="flex items-center justify-between">
        <span className={`chip rar-${d.rarity}`}>{d.rarity}</span>
        <span className="chip text-bx-gold border-bx-gold/40">
          <Crown size={12} weight="fill" /> {remaining}/{d.supply} left
        </span>
      </div>
      <div className="relative my-6 h-48 grid place-items-center">
        <div
          className="absolute inset-0 rounded-full blur-3xl opacity-30 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #ffd700, transparent 65%)' }}
        />
        <div className="relative h-44 w-44 rounded-full breathe spin-slow"
          style={{
            background: `conic-gradient(from 0deg, #ffd700, #ff5f00, #ff007f, #ffd700)`,
            filter: 'blur(2px)',
          }}
        />
        <div className="absolute h-36 w-36 rounded-full breathe overflow-hidden"
          style={{
            background: `radial-gradient(circle at 30% 25%, rgba(255,255,255,0.9), transparent 25%), radial-gradient(circle at 50% 50%, #ff5f00, #ff007f 60%, #7a00ff 100%)`,
            boxShadow: 'inset 0 0 60px rgba(255,255,255,0.18), 0 0 60px #ff5f00aa',
          }}
        />
      </div>
      <h3 className="font-display text-2xl font-black tracking-tighter">{d.name}</h3>
      <p className="text-white/65 text-sm mt-1 flex-1">{d.tagline}</p>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">
          Pick a cause ({d.split_pct_to_cause}% of price goes to it)
        </div>
        <div className="flex flex-wrap gap-1.5">
          {causes.map((c) => (
            <CauseChip
              key={c.id}
              label={c.tag}
              active={cause === c.id}
              onClick={() => setCause(c.id)}
              testId={`drop-${d.id}-cause-${c.id}`}
            />
          ))}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <div>
          <div className="font-display font-black text-3xl shimmer-text">${d.price_usd}</div>
          <div className="text-[10px] uppercase tracking-widest text-white/60">
            ${(d.price_usd * d.split_pct_to_cause / 100).toFixed(2)} to cause
          </div>
        </div>
        <button
          data-testid={`drop-buy-${d.id}`}
          disabled={soldOut}
          onClick={() => onBuy(d.id, cause)}
          className="btn-primary px-5 py-3 rounded-full text-sm flex items-center gap-2 disabled:opacity-30"
        >
          {soldOut ? 'Sold out' : <>Mint <ArrowRight size={14} weight="bold" /></>}
        </button>
      </div>
    </motion.div>
  );
};

const Drops = () => {
  const nav = useNavigate();
  const [drops, setDrops] = useState([]);
  const [causes, setCauses] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/drops').then(({ data }) => setDrops(data.drops));
    api.get('/charities').then(({ data }) => setCauses(data.charities));
  }, []);

  const buy = async (drop_id, cause_id) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post('/drops/checkout', {
        drop_id,
        cause_id,
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'checkout failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 pb-24">
      <div className="text-center mb-10">
        <ShoppingBag size={36} weight="duotone" className="text-bx-gold mx-auto" />
        <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tighter mt-2">
          Founder <span className="shimmer-text">Drops</span>
        </h1>
        <p className="text-white/65 mt-2 max-w-2xl mx-auto">
          Limited-supply, AI-generated legendary bubbles. A share of every purchase routes directly to the cause you pick. Hash-chained, founder-numbered, forever yours.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {drops.map((d) => (
          <DropCard key={d.id} d={d} causes={causes} onBuy={buy} />
        ))}
      </div>

      <div className="mt-12 text-center text-xs text-white/60">
        <Lightning size={12} weight="fill" className="inline text-bx-volt" />{' '}
        Payments are processed by Stripe. Cause splits are recorded on the public BubbleX ledger.
      </div>
    </div>
  );
};

export default Drops;
